import { initAxios } from '../../../utils/axiosHelper';

export interface EntityDocument {
  id: string;
  database_entity_id: string;
  database_id: string;
  group_id: string;
  file_name: string;
  content_type: string | null;
  file_size: number | null;
  uploaded_by: string | null;
  uploaded_by_name: string | null;
  created_at: string;
  source: EntityDocumentSource;
  /** The Saved Work ticket a filed report came from. */
  source_ref: string | null;
}

/** Where a Library file came from. */
export type EntityDocumentSource =
  | 'upload'
  | 'beneficial_ownership'
  | 'entity_risk';

const basePath = (databaseId: string, databaseEntityId: string) =>
  `/bite-db/database/${encodeURIComponent(
    databaseId,
  )}/entity/${encodeURIComponent(databaseEntityId)}/documents`;

export const getEntityDocuments = async (
  databaseId: string,
  databaseEntityId: string,
): Promise<EntityDocument[]> => {
  const response = await initAxios().get(
    basePath(databaseId, databaseEntityId),
  );
  return response.data?.data || [];
};

const multipartClient = () => {
  const client = initAxios();
  // Let the browser add the multipart boundary instead of inheriting the
  // JSON default from initAxios.
  delete client.defaults.headers.common['Content-Type'];
  delete (client.defaults.headers.post as Record<string, unknown>)[
    'Content-Type'
  ];
  delete (client.defaults.headers as Record<string, unknown>)['Content-Type'];
  return client;
};

export const uploadEntityDocument = async (
  databaseId: string,
  databaseEntityId: string,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<EntityDocument[]> => {
  const client = multipartClient();
  const formData = new FormData();
  formData.append('file', file);
  const response = await client.post(
    basePath(databaseId, databaseEntityId),
    formData,
    {
      onUploadProgress: (event: any) => {
        if (!event.total || !onProgress) return;
        onProgress(
          Math.min(100, Math.round((event.loaded * 100) / event.total)),
        );
      },
    },
  );
  return response.data?.data || [];
};

/** What came of asking the server to file a finished investigation's report. */
export type InvestigationFiling = 'filed' | 'already-filed' | 'no-report';

/**
 * Asks the server to build a finished Beneficial Ownership investigation's
 * report and file it to the record's Library. pyapi files it as the run ends;
 * this is the record page's backstop. Filing twice files once.
 */
export const fileInvestigationReport = async (
  databaseId: string,
  databaseEntityId: string,
  ticketId: string,
): Promise<InvestigationFiling> => {
  try {
    const response = await initAxios().post(
      `${basePath(databaseId, databaseEntityId)}/investigation`,
      { ticketId: ticketId },
    );
    return response.data?.data?.filed ? 'filed' : 'already-filed';
  } catch (error: any) {
    // Not a finished investigation of this record with a report to file.
    if (error?.response?.status === 404) return 'no-report';
    throw error;
  }
};

/**
 * The file name a download response carries, if any: the UTF-8 `filename*`
 * (entity names are often not Latin) before the ASCII `filename` fallback.
 */
export const attachmentName = (disposition: unknown): string | null => {
  const header = String(disposition || '');
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // Malformed encoding: fall through to the plain name.
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1] : null;
};

/**
 * Downloads a finished investigation's report PDF, built by the server: the
 * same document the run filed to the Library.
 */
export const downloadInvestigationReport = async (
  databaseId: string,
  databaseEntityId: string,
  ticketId: string,
): Promise<void> => {
  const response = await initAxios().get(
    `${basePath(
      databaseId,
      databaseEntityId,
    )}/investigation/${encodeURIComponent(ticketId)}/pdf`,
    { responseType: 'blob' },
  );
  const url = URL.createObjectURL(response.data as Blob);
  const anchor = window.document.createElement('a');
  anchor.href = url;
  anchor.download =
    attachmentName(response.headers?.['content-disposition']) ||
    'Beneficial Ownership.pdf';
  window.document.body.appendChild(anchor);
  anchor.click();
  window.document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

export const downloadEntityDocument = async (
  databaseId: string,
  document: EntityDocument,
): Promise<void> => {
  const response = await initAxios().get(
    `/bite-db/database/${encodeURIComponent(
      databaseId,
    )}/entity-documents/${encodeURIComponent(document.id)}/download`,
    { responseType: 'blob' },
  );
  const url = URL.createObjectURL(response.data as Blob);
  const anchor = window.document.createElement('a');
  anchor.href = url;
  anchor.download = document.file_name;
  window.document.body.appendChild(anchor);
  anchor.click();
  window.document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

export const deleteEntityDocument = async (
  databaseId: string,
  documentId: string,
): Promise<void> => {
  await initAxios().delete(
    `/bite-db/database/${encodeURIComponent(
      databaseId,
    )}/entity-documents/${encodeURIComponent(documentId)}`,
  );
};
